import React, { useEffect, useRef, useState } from 'react';
// RN `Text`, not the `@/components/ui` barrel: this is a print presenter imported by
// renderers whose specs mock only react-native, and the barrel drags expo-router in.
import { Image, Platform, StyleSheet, Text, View, type ViewStyle } from 'react-native';

import { fetchDocumentObjectUrl } from '@/utils/document-download';

/** Longest a fetched photo may take to load before the page prints without waiting for it. */
const IMAGE_SETTLE_TIMEOUT_MS = 20_000;

/**
 * A printable image.
 *
 * The bytes are fetched with our auth header and handed over as a blob URL — `<img src>`
 * cannot send headers, so a direct URL would print an empty frame. `onSettled` fires once,
 * after the image has LOADED (or definitively failed), so the page's auto-print can wait for
 * every image and never print a blank box where the photo was.
 *
 * Goes through `/attachments/:id/content` rather than `/files/<storageKey>` because the
 * storage key is not always the live source; the content route picks it server-side.
 *
 * Shared by the board packet (exterior applications) and the work-order print page (#498).
 */
export const PrintImage: React.FC<{
	attachmentId: string;
	caption?: string | null;
	/** Frame height in px. A packet page is read at arm's length; a photo appendix fits two per sheet. */
	height?: number;
	onSettled: () => void;
}> = ({ attachmentId, caption, height = 520, onSettled }) => {
	const [url, setUrl] = useState<string | null>(null);
	const [failed, setFailed] = useState(false);

	/**
	 * Settle exactly ONCE, and only when the pixels are paintable.
	 *
	 * Settling when the FETCH resolved was too early: the `<Image>` had not even mounted, so a
	 * large photo could still be decoding when the page printed — or when the server-side PDF
	 * renderer (#581) snapshotted it — leaving a blank frame. The image's own load/error event
	 * is the real signal; the timeout only guarantees a stuck decode can't block printing forever.
	 */
	const settledRef = useRef(false);
	const settle = () => {
		if (settledRef.current) return;
		settledRef.current = true;
		onSettled();
	};

	useEffect(() => {
		let objectUrl: string | null = null;
		let cancelled = false;
		(async () => {
			try {
				const next = await fetchDocumentObjectUrl(`/attachments/${attachmentId}/content`);
				if (cancelled) { URL.revokeObjectURL(next); return; }
				objectUrl = next;
				setUrl(next);
			} catch {
				// A missing photo must not stop the page printing — but it must not print a
				// half-sheet of empty grey either (seen on rows whose R2 object is gone).
				if (!cancelled) {
					setFailed(true);
					settle();
				}
			}
		})();
		return () => {
			cancelled = true;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- onSettled is a stable counter bump; including it would refetch every image on each settle
	}, [attachmentId]);

	useEffect(() => {
		if (!url) return;
		const fallback = setTimeout(settle, IMAGE_SETTLE_TIMEOUT_MS);
		return () => clearTimeout(fallback);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- settle is ref-guarded; only a new URL restarts the clock
	}, [url]);

	if (failed) {
		return (
			<Text style={styles.unavailable} testID={`print-image-unavailable-${attachmentId}`}>
				Photo unavailable{caption ? ` — ${caption}` : ''}
			</Text>
		);
	}

	return (
		<View style={styles.wrapper}>
			<View style={[styles.frame, { height }]}>
				{url ? (
					<Image
						source={{ uri: url }}
						style={styles.image}
						resizeMode="contain"
						onLoad={settle}
						onError={() => {
							// Bytes arrived but will not decode — same one-line note as missing bytes.
							setFailed(true);
							settle();
						}}
					/>
				) : null}
			</View>
			{caption ? (
				<Text style={styles.caption} numberOfLines={2}>{caption}</Text>
			) : null}
		</View>
	);
};

const styles = StyleSheet.create({
	wrapper: {
		width: '100%',
		marginBottom: 16,
		// Keep the frame and its caption on one sheet — a caption orphaned onto the next
		// page reads as belonging to the wrong photo.
		...(Platform.OS === 'web' ? { breakInside: 'avoid', pageBreakInside: 'avoid' } : {}),
	} as ViewStyle,
	frame: {
		backgroundColor: '#f2f2f2',
		alignItems: 'center',
		justifyContent: 'center',
	},
	image: {
		width: '100%',
		height: '100%',
	},
	caption: {
		fontSize: 11,
		color: '#555',
		marginTop: 4,
	},
	unavailable: {
		fontSize: 11,
		color: '#777',
		fontStyle: 'italic',
		marginBottom: 12,
	},
});
